import styled, { css } from 'styled-components';
import { fontUi, pill } from '../../../../theme/typography';

const mobileControl = css`
  height: 40px;
  border-radius: ${({ theme }) => theme.controlRadius};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
`;

export const StyledOpenFilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 34px;
  background-color: ${({ theme }) => theme.card};
  width: ${({ $inline }) => ($inline ? '100%' : '120px')};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.border};
  cursor: pointer;
  position: ${({ $inline }) => ($inline ? 'relative' : 'sticky')};
  top: 8px;
  z-index: 6;
  margin-left: ${({ $inline }) => ($inline ? '0' : 'auto')};
  margin-bottom: ${({ $inline }) => ($inline ? '0' : '10px')};
  @media only screen and (max-width: 768px) {
    width: ${({ $inline }) => ($inline ? '100%' : '95px')};
    height: ${({ $inline }) => ($inline ? '40px' : '28px')};
  }
  ${({ $inline }) =>
    $inline &&
    css`
      ${mobileControl};
      top: 0;
      z-index: 1;
    `}
  > span {
    margin: 0 10px;
    font-family: 'Archivo', sans-serif;
    font-size: ${({ $inline }) => ($inline ? '13px' : '12px')};
    line-height: 26px;
    font-weight: ${({ $inline }) => ($inline ? 600 : 500)};
    color: ${({ theme }) => theme.text};
    white-space: nowrap;
  }
`;

export const StyledOpenFilterButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 26px;
  border-left: 1px solid ${({ theme }) => theme.border};
  background: transparent;
  padding: 3px 0;
  ${({ $plain }) =>
    $plain &&
    css`
      width: 32px;
      height: 100%;
      border-left: 0;
      padding: 0;
    `}
  svg,
  svg path {
    fill: ${({ theme }) => theme.text};
  }
`;

export const StyledFilterContainer = styled.section`
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;
export const StyledManipulatorContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 48px 10px 0;
  ${({ $inline }) =>
    $inline &&
    css`
      padding: 0;
      margin: 0;
      width: 100%;
      box-sizing: border-box;
      background: transparent;
      border: 0;
      border-radius: 0;
    `}
  ${({ openFilter, $sheet }) => {
    if (!$sheet && openFilter === 'false') {
      return css`
        display: none;
      `;
    }
  }}
`;

export const StyledFilterTabs = styled.div`
  display: flex;
  width: 100%;
  margin: 0 0 8px;
  gap: 3px;
  padding: 2px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.hover};
  box-shadow: none;
`;

export const StyledFilterTab = styled.button`
  ${pill};
  ${fontUi};
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  text-box: unset;
  text-box-trim: none;
  color: ${({ theme }) => theme.textFaint};
  border: 0;
  outline: 0;
  padding: 6px 8px;
  cursor: pointer;
  background: transparent;

  svg {
    display: block;
    margin: 0;
    width: 11px;
    height: 11px;
    flex-shrink: 0;
  }

  &.active {
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.card};
  }
`;

export const StyledFilterField = styled.div`
  width: 100%;
`;

export const StyledFilterActions = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  padding: 4px 0 12px;
  gap: 8px;
`;

export const StyledClearButton = styled.button`
  ${pill};
  ${fontUi};
  min-width: 72px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
`;

export const StyledInlineWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  margin-bottom: 10px;
`;

export const StyledInlineRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  > * {
    flex: 1;
    min-width: 0;
  }
`;

export const StyledSheetScrim = styled.button`
  position: fixed;
  inset: 0;
  z-index: 40;
  margin: 0;
  padding: 0;
  border: 0;
  background: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.28s ease;
  ${({ $open }) =>
    $open &&
    css`
      opacity: 1;
      pointer-events: auto;
    `}
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const StyledSheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 41;
  display: flex;
  flex-direction: column;
  max-height: min(78vh, 640px);
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  border-top-left-radius: ${({ theme }) => theme.cardRadius};
  border-top-right-radius: ${({ theme }) => theme.cardRadius};
  box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.28);
  padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  overflow: auto;
  transform: translate3d(0, 110%, 0);
  transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
  ${({ $open }) =>
    $open &&
    css`
      transform: translate3d(0, 0, 0);
    `}
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const StyledSheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  h2 {
    ${fontUi};
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
  }
`;

export const StyledFab = styled.button`
  position: fixed;
  right: 16px;
  bottom: calc(40px + env(safe-area-inset-bottom, 0px));
  z-index: 20;
  width: 52px;
  height: 52px;
  margin: 0;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.headerControl};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.header};
  color: ${({ theme }) => theme.headerAccent};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.32);
  cursor: pointer;
  svg {
    display: block;
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
  }
`;

export const StyledFabBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: ${({ theme }) => theme.headerAccent};
  color: ${({ theme }) => theme.header};
  font-family: 'Archivo', sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;

export const StyledSortList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const StyledSortOption = styled.button`
  ${fontUi};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.headerAccent : theme.border)};
  border-radius: ${({ theme }) => theme.controlRadius};
  background: ${({ theme, $active }) => ($active ? theme.hover : theme.card)};
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
`;
